import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Calendar, Shield, Edit, Save, Camera, MapPin, Phone } from 'lucide-react';
import { toastService } from '../utils/toast';

const Profile: React.FC = () => {
  const { currentUser, isAdmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: currentUser?.displayName || '',
    email: currentUser?.email || '',
    photoURL: currentUser?.photoURL || '',
    bio: 'Billiárd szerető játékos',
    location: 'Budapest, Magyarország',
    phone: '+36 20 123 4567'
  });

  const handleSave = async () => {
    try {
      // Itt mentenénk a Firebase-be
      setIsEditing(false);
      toastService.success('Profil sikeresen frissítve');
    } catch (error) {
      toastService.error('Hiba történt a profil mentésekor');
    }
  };

  const handleCancel = () => {
    setProfileData({
      displayName: currentUser?.displayName || '',
      email: currentUser?.email || '',
      photoURL: currentUser?.photoURL || '',
      bio: 'Billiárd szerető játékos',
      location: 'Budapest, Magyarország',
      phone: '+36 20 123 4567'
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Profil</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Személyes adatok és beállítások kezelése
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2">
          {isEditing ? (
            <>
              <button onClick={handleCancel} className="btn-secondary">
                Mégse
              </button>
              <button onClick={handleSave} className="btn-primary">
                <Save className="mr-2 h-4 w-4" />
                Mentés
              </button>
            </>
          ) : (
            <button onClick={() => setIsEditing(true)} className="btn-primary">
              <Edit className="mr-2 h-4 w-4" />
              Szerkesztés
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Profile Header */}
        <div className="card">
          <div className="card-body">
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <div className="relative">
                <img
                  className="h-24 w-24 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
                  src={profileData.photoURL || 'https://via.placeholder.com/96'}
                  alt="Profil kép"
                />
                {isEditing && (
                  <button className="absolute bottom-0 right-0 bg-blue-600 rounded-full p-2 text-white hover:bg-blue-700 transition-colors duration-200">
                    <Camera className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="text-center sm:text-left">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {profileData.displayName}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{profileData.email}</p>
                {isAdmin && (
                  <span className="inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Személyes adatok</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="form-label">Teljes név</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.displayName}
                    onChange={(e) => setProfileData({...profileData, displayName: e.target.value})}
                    className="form-input"
                  />
                ) : (
                  <p className="text-sm text-gray-900 dark:text-white">{profileData.displayName}</p>
                )}
              </div>

              <div>
                <label className="form-label">Email cím</label>
                <p className="text-sm text-gray-900 dark:text-white flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-gray-400" />
                  {profileData.email}
                </p>
              </div>

              <div>
                <label className="form-label">Telefonszám</label>
                {isEditing ? (
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      className="form-input pl-10"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-900 dark:text-white flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    {profileData.phone}
                  </p>
                )}
              </div>

              <div>
                <label className="form-label">Lakhely</label>
                {isEditing ? (
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={profileData.location}
                      onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                      className="form-input pl-10"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-900 dark:text-white flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                    {profileData.location}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="form-label">Rólam</label>
                {isEditing ? (
                  <textarea
                    rows={3}
                    value={profileData.bio}
                    onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                    className="form-input"
                  />
                ) : (
                  <p className="text-sm text-gray-900 dark:text-white">{profileData.bio}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Account Stats */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Fiók statisztikák</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
                <div className="flex items-center">
                  <User className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Játékok</p>
                    <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">127</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                <div className="flex items-center">
                  <Shield className="h-8 w-8 text-green-600 dark:text-green-400" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">Győzelmek</p>
                    <p className="text-2xl font-semibold text-green-900 dark:text-green-100">89</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
                <div className="flex items-center">
                  <Calendar className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Regisztráció</p>
                    <p className="text-2xl font-semibold text-purple-900 dark:text-purple-100">2024</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Legutóbbi tevékenységek</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <Shield className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Játék megnyert
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">2 órával ezelőtt</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Új játék indult
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">1 nappal ezelőtt</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Profil frissítve
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">3 nappal ezelőtt</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 